import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MonitorSmartphone, RotateCw, Search, X } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { FilterMenu } from '@/components/ui/FilterMenu';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/Feedback';
import { StatusLegend, statusLabel } from '@/components/ui/StatusBadge';
import { DeviceTable, type SortState } from './DeviceTable';
import {
  DEFAULT_DEVICE_SEARCH,
  DEVICE_PAGE_SIZE,
  hasActiveFilters,
  type DeviceSearchParams,
} from './device-search';
import { listDeviceTags, listDevices } from '@/lib/api/endpoints/devices';
import { listOrganizations } from '@/lib/api/endpoints/organizations';
import { queryKeys } from '@/lib/query-keys';
import { formatNumber } from '@/lib/format';
import { DEVICE_PLATFORMS, DEVICE_PRESENCE_STATES, type DevicePlatform, type DevicePresenceState } from '@/lib/api/types';

/*
  The device list page.

  Search is debounced by 250 milliseconds before it reaches the URL and therefore the API.
  Typing "ACME" without a debounce is four requests and three discarded renders; the delay is
  short enough that it reads as instant and long enough that it collapses a word into one
  call.

  keepPreviousData holds the old rows on screen while a new page or sort is fetching. The
  table dims instead of collapsing to a spinner, which keeps the scroll position and keeps
  the row under the cursor where it was.

  The result count lives in an aria-live region. A sighted user sees the table change; a
  screen reader user gets told "24 devices match" after the filter lands, which is the piece
  that is normally missing.
*/

export interface DevicesPageProps {
  readonly search: DeviceSearchParams;
  readonly onSearchChange: (next: Partial<DeviceSearchParams>) => void;
}

export function DevicesPage({ search, onSearchChange }: DevicesPageProps): React.JSX.Element {
  const [searchText, setSearchText] = useState(search.q);

  // Keep the box in step when the URL changes from somewhere else, for example the
  // dashboard linking in with a device name already applied, or the back button.
  //
  // This is React's documented "adjusting state during render" pattern rather
  // than an effect. Doing it in an effect renders the stale value once, then
  // immediately renders again with the corrected one, which is both a wasted
  // pass and a visible flash of the previous query on a slow device.
  const [lastAppliedQuery, setLastAppliedQuery] = useState(search.q);
  if (search.q !== lastAppliedQuery) {
    setLastAppliedQuery(search.q);
    setSearchText(search.q);
  }

  useEffect(() => {
    if (searchText === search.q) {
      return;
    }
    const timer = window.setTimeout(() => {
      onSearchChange({ q: searchText, page: 1 });
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [onSearchChange, search.q, searchText]);

  const query = useMemo(
    () => ({
      search: search.q,
      states: search.states,
      organizationIds: search.orgs,
      platforms: search.platforms,
      tags: search.tags,
      sort: search.sort,
      direction: search.dir,
      limit: DEVICE_PAGE_SIZE,
      offset: (search.page - 1) * DEVICE_PAGE_SIZE,
    }),
    [search],
  );

  const devices = useQuery({
    queryKey: queryKeys.devices(query),
    queryFn: () => listDevices(query),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const organizations = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: listOrganizations,
    staleTime: 5 * 60_000,
  });

  const tags = useQuery({
    queryKey: queryKeys.deviceTags,
    queryFn: listDeviceTags,
    staleTime: 5 * 60_000,
  });

  const sort: SortState = { key: search.sort, direction: search.dir };
  const total = devices.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / DEVICE_PAGE_SIZE));
  const rows = devices.data?.items ?? [];
  const filtersActive = hasActiveFilters(search);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Devices"
        description="Every enrolled endpoint this account is authorized to see. Filters and sort are kept in the address bar, so a narrowed view can be shared as a link."
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              void devices.refetch();
            }}
            loading={devices.isFetching && !devices.isPending}
          >
            <RotateCw className="size-4" aria-hidden="true" />
            Refresh
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
              aria-hidden="true"
            />
            <input
              type="search"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
              }}
              // An accessible name without a visible label, because the magnifier plus the
              // placeholder already communicate it to a sighted user and a second visible
              // label would be noise in a filter bar.
              aria-label="Search devices by name, organization, site, user, IP, agent version or tag"
              placeholder="Search devices, users, IP addresses, tags"
              className="h-9 w-full rounded-[var(--radius-control)] border border-border bg-surface pr-3 pl-9 text-sm text-fg placeholder:text-fg-subtle hover:border-border-strong"
            />
          </div>

          <FilterMenu
            label="Status"
            selected={search.states}
            options={DEVICE_PRESENCE_STATES.map((state) => ({
              value: state,
              label: statusLabel(state),
            }))}
            onChange={(selected) => {
              onSearchChange({ states: selected as readonly DevicePresenceState[], page: 1 });
            }}
          />

          <FilterMenu
            label="Organization"
            selected={search.orgs}
            options={(organizations.data ?? []).map((org) => ({
              value: org.id,
              label: org.name,
              ...(org.status === 'active' ? {} : { hint: org.status }),
            }))}
            onChange={(selected) => {
              onSearchChange({ orgs: selected, page: 1 });
            }}
          />

          <FilterMenu
            label="Platform"
            selected={search.platforms}
            options={DEVICE_PLATFORMS.map((platform) => ({
              value: platform,
              label: platform === 'macos' ? 'macOS' : platform.charAt(0).toUpperCase() + platform.slice(1),
            }))}
            onChange={(selected) => {
              onSearchChange({ platforms: selected as readonly DevicePlatform[], page: 1 });
            }}
          />

          <FilterMenu
            label="Tags"
            selected={search.tags}
            options={(tags.data ?? []).map((tag) => ({ value: tag, label: tag }))}
            onChange={(selected) => {
              onSearchChange({ tags: selected, page: 1 });
            }}
          />

          {filtersActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchText('');
                onSearchChange({ ...DEFAULT_DEVICE_SEARCH, sort: search.sort, dir: search.dir });
              }}
            >
              <X className="size-3.5" aria-hidden="true" />
              Clear filters
            </Button>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
          <p aria-live="polite" className="text-xs text-fg-muted">
            {devices.isPending ? (
              <Spinner label="Loading devices" />
            ) : (
              <>
                <span className="font-semibold text-fg tabular-nums">{formatNumber(total)}</span>{' '}
                {total === 1 ? 'device' : 'devices'}
                {filtersActive ? ' match the current filters' : ' enrolled'}
                {pageCount > 1
                  ? `, page ${formatNumber(search.page)} of ${formatNumber(pageCount)}`
                  : ''}
              </>
            )}
          </p>
          {devices.isFetching && !devices.isPending ? <Spinner label="Updating" /> : null}
        </div>

        {devices.isError ? (
          <div className="p-4">
            <Alert tone="danger" title="Could not load devices" liveness="assertive">
              {devices.error instanceof Error ? devices.error.message : 'Unknown error.'}
            </Alert>
          </div>
        ) : devices.isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<MonitorSmartphone className="size-5" aria-hidden="true" />}
            title={filtersActive ? 'No devices match these filters' : 'No devices enrolled yet'}
            description={
              filtersActive
                ? 'Widen or clear the filters to see more of the fleet.'
                : 'Mint an enrollment token for an organization, run the agent installer on an endpoint, and it will appear here within seconds of its first gateway connection.'
            }
            action={
              filtersActive ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearchText('');
                    onSearchChange({ ...DEFAULT_DEVICE_SEARCH, sort: search.sort, dir: search.dir });
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <DeviceTable
            rows={rows}
            sort={sort}
            refreshing={devices.isFetching && !devices.isPending}
            onSortChange={(next) => {
              onSearchChange({ sort: next.key, dir: next.direction, page: 1 });
            }}
          />
        )}

        {pageCount > 1 ? (
          <nav
            aria-label="Device list pages"
            className="flex items-center justify-between gap-3 border-t border-border px-3 py-2"
          >
            <Button
              variant="secondary"
              size="sm"
              disabled={search.page <= 1}
              onClick={() => {
                onSearchChange({ page: search.page - 1 });
              }}
            >
              Previous
            </Button>
            <span className="text-xs text-fg-muted tabular-nums">
              Page {formatNumber(search.page)} of {formatNumber(pageCount)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={search.page >= pageCount}
              onClick={() => {
                onSearchChange({ page: search.page + 1 });
              }}
            >
              Next
            </Button>
          </nav>
        ) : null}
      </Card>

      <Card>
        <div className="p-4">
          <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
            Status reference
          </h2>
          <StatusLegend />
        </div>
      </Card>
    </div>
  );
}
